import React from 'react';

import { COMMERCE_GATE_PROVIDER, GATE2WAY_PROVIDER, STRIPE_PROVIDER } from '@/utils/session-invoice';
import { useThemeMode } from '@/nova/theme/OwsTheme';
import { PREVIEW_LOADER_SRC } from '../../../../components/wizard/PreviewPlaceholder';
import { CardFields } from './CardFields';
import { CheckoutError } from './CheckoutError';
import { PaymentMethodForm } from './PaymentMethodForm';
import { PixInstructions } from './PixInstructions';
import { SpeiInstructions } from './SpeiInstructions';
import type { CheckoutState } from './useCheckout';
import { CardStatusBox, CardStatusText, RetryButton, RetryLabel, Spinner } from './PaymentMethodForm.styles';

export type CheckoutBodyProps = {
  checkout: CheckoutState;
  /** Amount due today (voucher already subtracted) for the form footer + amount fallback. */
  amount: number;
  currency: string;
  currencySymbol: string;
  totalLabel: string;
  totalSub?: string;
  /** The payment is made: acknowledge it, then show the confirmation. Shared by both rails —
   *  the buyer's "I've made the transfer", and a cleared card charge. `cardSettled` is true only
   *  for a card charge that CLEARED (the transfer, and a card still processing, pass false). Read
   *  it from the argument, never from `checkout.cardSettled`: this fires from the Stripe attempt's
   *  continuation, whose closure predates that state. */
  onPaid: (cardSettled: boolean) => void;
  /** Rendered in the mobile sheet: drops the section headings and the intro's scene-setting
   *  sentence, which the sheet's own title and the scarcity of vertical space make redundant. */
  compact?: boolean;
};

/**
 * WHICH screen the checkout is showing, for both rails.
 *
 * Extracted because the desktop pane and the mobile sheet render exactly this and differ only in
 * their chrome. Four call sites across two flows had already drifted apart once; a single body
 * means a rail added here cannot be missing there.
 *
 * The order of the branches is the contract:
 *  1. **restoring** — the entry read has not settled, so we do not yet know which rail the buyer
 *     was on. Showing the default first and swapping it a beat later reads as the checkout
 *     losing their choice, and on the transfer rail it hides coordinates they already hold.
 *  1b. **load failed** — there is a session id but the session behind it never loaded. Ranked
 *     above the form because the form would be PAYABLE against empty data: a submit overwrites
 *     the buyer's real selection and reissues their invoice against it. Retryable, so it offers
 *     one — that is what separates it from `no session` below.
 *  2. **no session** — nothing to bill; a refetch can't bring one back, so no retry is offered.
 *  2b. **back confirm** (Pix): Back was pressed on a Pix code. Leaving abandons a code the buyer
 *     may already have paid, so they confirm first. Hook state, so every shell's Back gets it.
 *  3. **instructions by rail**: the SPEI coordinates (`commerce-gate`) or the Pix code
 *     (`gate2way`). Either one outranks the form.
 *  4. **the method form** — choose a rail and fill it in. The card's Stripe fields slot INTO it
 *     rather than replacing it, so becoming chargeable never remounts the form (and never blanks
 *     the email the buyer may be editing while it loads).
 */
export const CheckoutBody = ({
  checkout,
  amount,
  currency,
  currencySymbol,
  totalLabel,
  totalSub,
  onPaid,
  compact,
}: CheckoutBodyProps): React.ReactElement => {
  // Studio theme: the loader art is white on the dark pane, darkened on the light one.
  const onLight = useThemeMode() === 'light';
  const onInstructions = checkout.view === 'instructions' && checkout.details;
  const onCard = checkout.method === STRIPE_PROVIDER;

  // ONE submit for both rails, because there is one button. The card runs its Stripe attempt and
  // finishes the checkout on a cleared charge; the transfer creates its invoice and moves on to
  // the coordinates screen (so it does NOT finish here).
  const { payCard, submit } = checkout;
  const onSubmit = React.useCallback(
    (email: string, fullName: string): void => {
      if (onCard) {
        void payCard().then((charge) => {
          if (charge !== 'failed') onPaid(charge === 'settled');
        });
        return;
      }
      submit(email, fullName);
    },
    [onCard, payCard, submit, onPaid],
  );

  // Auto-advance on payment (spec 4.7, Pix only: `paidWhileShown` is false on every other rail).
  // Fires once per mount, the same `onPaid(false)` as the "I've made the payment" button: the flows
  // compute `settled` from `invoiceStatus === 'paid'` themselves, so `false` is right. The
  // exactly-once acknowledge is the thunk's `condition`, not this ref. The ref IS load-bearing:
  // every consumer passes `onPaid` as an inline arrow (`PaymentPane`, `MobilePaymentFlow`, the v3
  // shells), so the deps change on every render and only the ref stops a re-fire. Do not simplify
  // it away. The button stays as the fallback. No polling.
  const autoPaidRef = React.useRef(false);
  React.useEffect(() => {
    if (!checkout.paidWhileShown || autoPaidRef.current) return;
    autoPaidRef.current = true;
    onPaid(false);
  }, [checkout.paidWhileShown, onPaid]);

  if (checkout.restoring) {
    return (
      <CardStatusBox>
        <Spinner $onLight={onLight} src={PREVIEW_LOADER_SRC} alt="" />
        <CardStatusText text="Restoring your checkout…" tag="paragraph" />
      </CardStatusBox>
    );
  }
  // The session could not be loaded. NOT the form: submitting against the empty session data
  // behind this state would overwrite the buyer's real selection server-side (`/session/save`
  // replaces `data` wholesale) and reissue their invoice against it, voiding a CLABE they may
  // already hold. Usually transient, so unlike `noSession` this one offers a retry.
  if (checkout.loadFailed) return <CheckoutError onRetry={checkout.retryLoad} />;
  if (checkout.noSession) return <CheckoutError noSession />;
  if (checkout.backConfirmPending) {
    return (
      <CardStatusBox>
        <CardStatusText
          text="If you already paid with this code, do not get a new one."
          tag="paragraph"
        />
        <RetryButton type="button" data-ga-id="cfg-mx-pix-back-confirm" onClick={checkout.confirmBack}>
          <RetryLabel text="Go back and get a new code" tag="span" />
        </RetryButton>
        <RetryButton type="button" data-ga-id="cfg-mx-pix-back-stay" onClick={checkout.dismissBack}>
          <RetryLabel text="Stay on this code" tag="span" />
        </RetryButton>
      </CardStatusBox>
    );
  }
  if (onInstructions && checkout.details?.rail === GATE2WAY_PROVIDER) {
    return (
      <PixInstructions
        details={checkout.details.pix}
        fallbackAmount={amount}
        fallbackCurrency={currency}
        onContinue={() => onPaid(false)}
        continuing={checkout.acknowledging}
        qaFinalizeUrl={checkout.qaFinalizeUrl}
        compact={compact}
      />
    );
  }
  if (onInstructions && checkout.details?.rail === COMMERCE_GATE_PROVIDER) {
    return (
      <SpeiInstructions
        details={checkout.details.spei}
        fallbackAmount={amount}
        fallbackCurrency={currency}
        onContinue={() => onPaid(false)}
        continuing={checkout.acknowledging}
        qaFinalizeUrl={checkout.qaFinalizeUrl}
      />
    );
  }
  return (
    <PaymentMethodForm
      methods={checkout.methods}
      method={checkout.method}
      onMethod={checkout.selectMethod}
      email={checkout.email}
      onEmail={checkout.setEmail}
      fullName={checkout.fullName}
      onFullName={checkout.setFullName}
      nameConfirmed={checkout.nameConfirmed}
      onConfirmName={checkout.confirmName}
      onEditName={checkout.editName}
      amount={amount}
      currencySymbol={currencySymbol}
      currencyCode={currency}
      totalLabel={totalLabel}
      totalSub={totalSub}
      submitting={checkout.submitting}
      error={checkout.error}
      cardPhase={checkout.card.phase}
      cardError={checkout.card.error}
      onCardRetry={checkout.card.retry}
      onCardConfirmSupersede={checkout.card.confirmSupersede}
      supersedes={checkout.details?.rail ?? null}
      cardSlot={
        onCard && checkout.card.clientSecret ? (
          <CardFields
            clientSecret={checkout.card.clientSecret}
            email={checkout.email}
            onRegisterPay={checkout.card.registerPay}
          />
        ) : null
      }
      compact={compact}
      onSubmit={onSubmit}
    />
  );
};

export default CheckoutBody;
